import { yupResolver } from '@hookform/resolvers/yup';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import {
  CreateMemberCampaignForm,
  memberCampaignSchema,
  MemberCampaignSchema,
} from '@retail/campaign/components';
import { useCreateMemberCampaign } from '@retail/campaign/data-access';
import { MemberCampaignPostDTO } from '@retail/campaign/types';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { CreateCampaignDialog } from './CreateCampaignDialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const CreateMemberCampaignContainer = ({ open, onClose }: Props) => {
  const t = useAppTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { assortmentId } = useSelectedOrgUnit();

  const { mutateAsync: mutateCreateMemberCampaignAsync, isPending: isLoadingCreate } =
    useCreateMemberCampaign({ assortmentId });

  const formMethods = useForm<MemberCampaignSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(memberCampaignSchema(t)),
    defaultValues: memberCampaignSchema(t).getDefault(),
  });

  const submitCampaign = formMethods.handleSubmit(async (schema: MemberCampaignSchema) => {
    try {
      const campaignPostDTO: MemberCampaignPostDTO = {
        campaignName: schema.campaignName,
        description: schema.description,
        validFrom: dayjs(schema.validFrom).format('YYYY-MM-DD'),
        validTo: dayjs(schema.validTo).format('YYYY-MM-DD'),
        purchasePeriodFrom: dayjs(schema.purchasePeriodFrom).format('YYYY-MM-DD'),
        purchasePeriodTo: dayjs(schema.purchasePeriodTo).format('YYYY-MM-DD'),
      };

      await mutateCreateMemberCampaignAsync({ body: campaignPostDTO });
      toast.success(t('campaign.messages.createCampaign'));

      queryClient.invalidateQueries({
        queryKey: ['campaigns'],
      });

      onClose();
    } catch (error) {
      console.error(error);
      toast.error(t('campaign.error.createCampaign'));
    }
  }, console.error);

  return (
    <FormProvider {...formMethods}>
      <CreateCampaignDialog
        open={open}
        onClose={onClose}
        isLoading={isLoadingCreate}
        onSubmit={submitCampaign}
      >
        <CreateMemberCampaignForm />
      </CreateCampaignDialog>
    </FormProvider>
  );
};
